import type { HapticType, InvoicePayResult, PlatformAdapter, PlatformInitResult } from './adapter'

/**
 * MAX Bridge adapter. Active when running inside the MAX mini-app host —
 * the bridge script (st.max.ru/js/max-web-app.js) sets `window.WebApp` with
 * a populated `initData`. Outside MAX `initData` is empty and detection
 * falls through to TG / Mock.
 *
 * Bridge surface differs from Telegram WebApp:
 *  - no ready()/expand()/close() — lifecycle is host-driven
 *  - haptics return Promises (we void them, semantics are identical)
 *  - external links open via openLink() / openMaxLink() (deep-link variant)
 */
export class MaxPlatformAdapter implements PlatformAdapter {
  readonly platform = 'max' as const

  private get bridge() {
    return window.WebApp
  }

  async init(): Promise<PlatformInitResult> {
    const wa = this.bridge
    if (!wa) throw new Error('MAX Bridge is not available')
    const u = wa.initDataUnsafe?.user
    return {
      initDataRaw: wa.initData,
      user: {
        id: u ? String(u.id) : 'unknown',
        firstName: u?.first_name,
        lastName: u?.last_name,
        username: u?.username,
        languageCode: u?.language_code,
      },
      theme: { colorScheme: 'light' },
    }
  }

  getLanguageHint(): string {
    return this.bridge?.initDataUnsafe?.user?.language_code ?? 'ru'
  }

  async share(payload: { url: string; text: string }): Promise<void> {
    // MAX bridge has no native share dialog yet. Fall back to opening a MAX
    // deep-link to the URL — host catches it and offers forward / copy.
    this.bridge?.openMaxLink?.(payload.url)
  }

  async openLink(url: string): Promise<void> {
    this.bridge?.openLink?.(url)
  }

  async openInvoice(url: string): Promise<InvoicePayResult> {
    // MAX has no native invoice bridge — the user is on the wrong platform
    // for paid features anyway (we re-route the paywall to TG before this
    // ever fires). For safety, open the URL externally so they at least
    // see the invoice.
    this.bridge?.openLink?.(url)
    return 'pending'
  }

  hapticFeedback(type: HapticType): void {
    const hf = this.bridge?.HapticFeedback
    if (!hf) return
    if (type === 'success' || type === 'warning' || type === 'error') {
      void hf.notificationOccurred(type)
    } else {
      void hf.impactOccurred(type)
    }
  }

  async showAlert(text: string): Promise<void> {
    alert(text)
  }

  closeApp(): void {
    // MAX has no programmatic close — host controls lifecycle.
  }

  getStartParam(): string | null {
    return this.bridge?.initDataUnsafe?.start_param ?? null
  }
}
